import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

const RANKED_SEARCH_TIMEOUT_MS = 10000;
const NATIVE_SEARCH_DELAY_MS = 2000;

/**
 * @param {string | null} href
 * @returns {string | null}
 */
function productHandleFromHref(href) {
  if (!href) return null;

  const parts = new URL(href, location.origin).pathname.split('/').filter(Boolean);
  return parts[0] === 'products' && parts[1] ? parts[1] : null;
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {AbortController | null} */
  #rankingAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#rankSearchResults();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#rankingAbortController?.abort();
  }

  async #rankSearchResults() {
    const rankingUrl = this.dataset.rankedSearchUrl;
    const pageUrl = new URL(location.href);
    const query = pageUrl.searchParams.get('q')?.trim() ?? '';
    const sortBy = pageUrl.searchParams.get('sort_by');
    const hasFilters = [...pageUrl.searchParams.keys()].some((key) => key.startsWith('filter.'));
    const { grid } = this.refs;

    if (
      !rankingUrl ||
      query.length < 2 ||
      hasFilters ||
      (sortBy && sortBy !== 'relevance') ||
      !(grid instanceof HTMLElement)
    ) {
      this.removeAttribute('data-ranked-search-pending');
      return;
    }

    const url = new URL(rankingUrl, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('view', 'order');

    const abortController = new AbortController();
    this.#rankingAbortController?.abort();
    this.#rankingAbortController = abortController;
    const timeout = setTimeout(() => abortController.abort(), RANKED_SEARCH_TIMEOUT_MS);
    const nativeFallbackTimeout = setTimeout(
      () => this.removeAttribute('data-ranked-search-pending'),
      NATIVE_SEARCH_DELAY_MS
    );

    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
        signal: abortController.signal,
      });
      if (!response.ok) return;

      const rankedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const rankedProducts = rankedDocument.querySelector(
        '#predictive-search-products[data-pichon-ranked-search]'
      );
      const rankedHandlesAttribute = rankedProducts?.getAttribute('data-ranked-product-handles');
      const rankedProductIdsAttribute = rankedProducts?.getAttribute('data-ranked-product-ids');
      const rankedHandles = rankedHandlesAttribute
        ? rankedHandlesAttribute.split('|').filter(Boolean)
        : Array.from(rankedProducts?.querySelectorAll('a[href*="/products/"]') ?? [])
            .map((link) => productHandleFromHref(link.getAttribute('href')))
            .filter((handle, index, handles) => handle && handles.indexOf(handle) === index);
      const rankedProductIds = rankedProductIdsAttribute?.split('|').filter(Boolean) ?? [];
      if (!rankedHandles?.length || abortController.signal.aborted) {
        return;
      }

      const rankedResultsUrl = new URL(Theme.routes.search_url, location.origin);
      const rankedProductQueries =
        rankedProductIds.length === rankedHandles.length
          ? rankedProductIds.map((productId) => `id:${productId}`)
          : rankedHandles.map((handle) => `handle:${handle}`);
      rankedResultsUrl.searchParams.set('q', rankedProductQueries.join(' OR '));
      rankedResultsUrl.searchParams.set('options[prefix]', 'last');
      const rankedResultsMarkup = await sectionRenderer.getSectionHTML(this.sectionId, false, rankedResultsUrl);
      if (!rankedResultsMarkup || abortController.signal.aborted) return;

      const rankedResultsDocument = new DOMParser().parseFromString(rankedResultsMarkup, 'text/html');
      const rankedGrid = rankedResultsDocument.querySelector('[ref="grid"]');
      if (!(rankedGrid instanceof HTMLElement)) return;

      const rankByHandle = new Map(rankedHandles.map((handle, index) => [handle, index]));
      const cards = Array.from(rankedGrid.querySelectorAll(':scope > li.product-grid__item[data-product-id]'));
      cards.sort((left, right) => {
        const leftHandle = productHandleFromHref(left.querySelector('a[href*="/products/"]')?.getAttribute('href') ?? null);
        const rightHandle = productHandleFromHref(
          right.querySelector('a[href*="/products/"]')?.getAttribute('href') ?? null
        );
        return (rankByHandle.get(leftHandle ?? '') ?? Number.MAX_SAFE_INTEGER) -
          (rankByHandle.get(rightHandle ?? '') ?? Number.MAX_SAFE_INTEGER);
      });
      if (!cards.length) return;

      grid.replaceChildren(...cards);
      grid.setAttribute('data-pichon-ranked-search', '');
    } catch {
      // Shopify's native order remains visible when ranking is unavailable.
    } finally {
      clearTimeout(timeout);
      clearTimeout(nativeFallbackTimeout);
      this.removeAttribute('data-ranked-search-pending');
      if (this.#rankingAbortController === abortController) {
        this.#rankingAbortController = null;
      }
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
